import React, {useCallback, useEffect, useMemo} from 'react';
import Head from 'next/head';
import {
  Box,
  Typography,
  FrameworkLink,
  NftGridList,
  ArtistCard,
} from '@ttx/design-system';
import {GetServerSideProps} from 'next';
import {NextPageWithPageLayoutOptions} from '../../components/page-layout/types';
import {MAX_NFT_GRID_COLUMNS, MIN_NFT_GRID_COLUMNS} from '../../utils/consts';
import {useTtxMutation} from '../../hooks/ttx-trpc';
import {SEO} from '../../components/seo';
import {useAuthenticationContext} from '../../contexts/authentication-context';
import {
  hasAccessToCollection,
  isWhitelistedCollection,
} from '../../utils/whitelistCollection';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ArtistsPageProps {
  url: string;
}

const ArtistsPage: NextPageWithPageLayoutOptions<ArtistsPageProps> = (
  props,
) => {
  const {walletAddress} = useAuthenticationContext();
  const {isLoading, error, ...collectionListMutation} =
    useTtxMutation('collection.list');

  useEffect(() => {
    collectionListMutation.mutate({
      limit: 0,
      page: 1,
      creator: undefined,
      collectionStatus: ['minted', 'draft'],
      nftStatus: undefined,
      artistSlug: undefined,
      includeFeatures: true,
      isCollaborators: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const artistData = useMemo(() => {
    const artists = {} as {
      [k: string]: {
        id: string;
        slug: string;
        artistName: string;
        artistHandle: string | null;
        artistImage: string;
        numOfCollections: number;
      };
    };

    collectionListMutation?.data
      ?.filter((collection) => {
        if (!collection.creatorDetails?.user?.slug) return false;

        if (isWhitelistedCollection(collection._id)) {
          if (!walletAddress) {
            return false;
          }
          const hasAccess = hasAccessToCollection(
            collection._id,
            walletAddress,
          );
          return hasAccess;
        }
        return true;
      })
      .forEach((item) => {
        artists[item.creatorDetails._id] = {
          id: item.creatorDetails._id,
          slug: item.creatorDetails.user.slug,
          artistName: item.creatorDetails.user.displayName,
          // artistHandle: item.creatorDetails.user.socialLinks?.twitter
          //   ? `@${item.creatorDetails.user.socialLinks?.twitter
          //       ?.split('/')
          //       .pop()}`
          //   : null,
          // TODO
          artistHandle: null,
          artistImage: item.creatorDetails.user.image,
          numOfCollections:
            (artists[item.creatorDetails._id]?.numOfCollections || 0) + 1,
        };
      });

    return Object.values(artists);
  }, [collectionListMutation.data]);

  const renderItem = useCallback((item: typeof artistData[number]) => {
    return (
      <FrameworkLink href={`/artists/${item.slug}`}>
        <ArtistCard
          artistName={item.artistName}
          artistHandle={item.artistHandle}
          numOfCollections={item.numOfCollections.toString()}
          src={item.artistImage}
        />
      </FrameworkLink>
    );
  }, []);

  return (
    <>
      <Head>
        <title>All artists</title>
      </Head>
      <SEO
        url={`${props.url}/artists`}
        title="All Artists"
        description="The main stage for music in Web3. Connecting artists and fans to mint the sound of a decentralised future."
      />
      <Box padding="lg" width="100%">
        <Typography textStyle={['h2', 'h2', 'h1']} color="textDefault">
          All artists
        </Typography>
      </Box>
      <Box style={{marginLeft: '-1px', marginRight: '-1px'}}>
        <NftGridList
          columns={
            artistData.length > 4 ? MAX_NFT_GRID_COLUMNS : MIN_NFT_GRID_COLUMNS
          }
          rowSpace="xxs"
          columnSpace="xxs"
          numberOfLoadingItems={12}
          keyExtractor={(item) => item.id}
          loading={isLoading}
          items={artistData}
          error={error}
          renderItem={renderItem}
        />
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  return {
    props: {
      url: context?.req?.headers?.host,
    },
  };
};

ArtistsPage.pageLayoutOptions = {
  showFooter: true,
};

export default ArtistsPage;
